import { Column, useTable } from 'react-table';
import * as React from 'react';
import classNames from 'classnames';

import * as Classes from '../common/classes';
import { useMemo } from 'react';
import {
  ClassicBaseTableProps,
  getCell,
  getFormattedValue,
  getTableAligment,
  getTableAligmentByType,
} from './TableProps';
import { NoData } from '../no-data/NoData';
import { TableMode } from '../common/props';
import { useCurrentTheme } from '../themes';

type Props<D extends object = {}> = {
  data: D[];
  mode?: TableMode;
} & ClassicBaseTableProps<D>;

export const Table = <D extends object = {}>({
  columns,
  data,
  className,
  rowDisableHover,
  getRowProps = () => ({}),
  mode = 'default',
}: Props<D>) => {
  const hasFooter = useMemo<boolean>(() => {
    for (const column of columns) {
      if (column.footer) {
        return true;
      }
    }
    return false;
  }, [columns]);

  const theme = useCurrentTheme();

  const formatedColumns: Column<D>[] = React.useMemo(
    () =>
      columns.map((column, index) => {
        const alignClass = classNames(
          column.align
            ? getTableAligment(column.align)
            : getTableAligmentByType(column?.type) || Classes.TABLE_ALIGN_LEFT,
          column.className,
          {
            [Classes.TABLE_CELL_ACTION]: column.type === 'action',
          }
        );

        const { type, align, fractionDigits, className, ...rest } = column;

        const columnCustom: any = {};
        if (!rest.accessor && !rest.id) {
          columnCustom.id = `col${index}`;
        }
        if (column.type === 'action' && column.width === undefined) {
          let size = column.actionSize || 1;
          const width =
            size * theme.iconButtonWidth +
            (size - 1) * theme.spacers[1] +
            theme.tableCellHorizontalPadding;
          columnCustom.width = width;
        }

        const formatedColumn: Column<D> = {
          ...rest,
          ...columnCustom,
          // @ts-ignore
          className: alignClass,
          // @ts-ignore
          ...((column.cell || column.type) && {
            // @ts-ignore
            Cell: (props) => {
              const cell = column.cell ? column.cell : getCell(column.type);
              return (
                cell?.({
                  rowIndex: props.row.index,
                  columnIndex: props.columnIndex,
                  row: props.row.original,
                  // @ts-ignore
                  column: column,
                  rows: data,
                  value: props.value,
                  formattedValue: column.type
                    ? getFormattedValue(column, props.value)
                    : undefined,
                }) || <></>
              );
            },
          }),
          ...(column.header && {
            Header: column.header,
          }),
          ...(column.footer && {
            Footer: () => {
              if (typeof column.footer === 'function') {
                return (
                  column.footer?.({
                    rows: data,
                  }) || <></>
                );
              } else {
                return column.footer;
              }
            },
          }),
        };
        return formatedColumn;
      }),
    [columns]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    footerGroups,
    rows,
    prepareRow,
  } = useTable<D>({
    columns: formatedColumns,
    data,
  });

  const tableProps = getTableProps();
  const useWrapper = mode === 'input';
  const wrapperCls = classNames(Classes.TABLE_WRAPPER, className, {
    [Classes.TABLE_MODE_INPUT]: mode === 'input',
  });
  const cls = classNames(Classes.TABLE, className, tableProps.className, {
    [Classes.TABLE_HAS_FOOTER]: hasFooter,
    [Classes.TABLE_MODE_DEFAULT]: mode === 'default',
    [Classes.TABLE_MODE_EDIT]: mode === 'edit',
    [Classes.TABLE_MODE_VIEW]: mode === 'view',
  });

  let tableComponent = (
    <table {...tableProps} className={cls}>
      <colgroup>
        {formatedColumns.map((column, index) => (
          <col
            style={column.width ? { width: column.width } : {}}
            key={index}
          />
        ))}
      </colgroup>
      <thead>
        {headerGroups.map((group) => (
          <tr {...group.getHeaderGroupProps()}>
            {group.headers.map((column) => (
              <th
                {...column.getHeaderProps([
                  {
                    // @ts-ignore
                    className: column.className,
                  },
                ])}
              >
                {column.render('Header')}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody {...getTableBodyProps()}>
        {rows?.length > 0 ? (
          rows.map((row, index) => {
            prepareRow(row);
            const customRowProps = getRowProps({
              row: row.original,
              rowIndex: index,
            });
            return (
              <tr
                style={customRowProps?.style}
                className={classNames(
                  customRowProps?.className,
                  Classes.HOVER_CONTAINER,
                  {
                    [Classes.HOVER_ITEM_CONTAINER]: !rowDisableHover,
                    [Classes.CLICK_CONTAINER]:
                      customRowProps?.onClick || customRowProps?.link,
                  }
                )}
                onClick={customRowProps?.onClick}
                key={index}
              >
                {row.cells.map((cell) => {
                  return (
                    <td
                      {...cell.getCellProps({
                        // @ts-ignore
                        className: cell.column.className,
                      })}
                    >
                      {cell.render('Cell')}
                    </td>
                  );
                })}
              </tr>
            );
          })
        ) : (
          <tr>
            <td colSpan={columns.length}>
              <NoData />
            </td>
          </tr>
        )}
      </tbody>
      {hasFooter && footerGroups && rows.length > 0 && (
        <tfoot>
          {footerGroups.map((group) => (
            <tr {...group.getFooterGroupProps()}>
              {group.headers.map((column) => (
                <td
                  {...column.getFooterProps({
                    // @ts-ignore
                    className: column.className,
                  })}
                >
                  {column.render('Footer')}
                </td>
              ))}
            </tr>
          ))}
        </tfoot>
      )}
    </table>
  );

  if (useWrapper) {
    return <div className={wrapperCls}>{tableComponent}</div>;
  } else {
    return tableComponent;
  }
};
